import React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { EVORA_COLORS } from '../../../theme/colors';
import EvoraLogo from '../AppBars/Logo/Evora.png';
import routes from '../../Routing/Routes';

// The Login "CONTINUE" button gradient: dark olive -> storm blue.
// Keep in sync with the `signIn` button variant in theme/index.js.
const EVORA_GRADIENT = `linear-gradient(45deg, ${EVORA_COLORS.darkOlive} 30%, ${EVORA_COLORS.stormBlue} 90%)`;

// Map a route's first path segment -> its friendly name, built once.
// `routes` is already in the initial bundle (DynamicRouter imports it), so this
// adds no load cost. Route components are lazy, so this Suspense fallback fires
// on each first navigation — letting us show "Loading <Screen>…" per page.
const ROUTE_NAME_BY_SEGMENT = routes.reduce((map, r) => {
  if (r && typeof r.path === 'string' && r.name) {
    const seg = r.path.replace(/^\//, '').split('/')[0];
    if (seg && !(seg in map)) map[seg] = r.name;
  }
  return map;
}, {});

// Derive "Loading <Screen>…" from the current hash route, or null if unknown.
function routeLabelFromHash() {
  if (typeof window === 'undefined') return null;
  const seg = (window.location.hash || '').replace(/^#/, '').replace(/^\//, '').split('/')[0];
  const name = ROUTE_NAME_BY_SEGMENT[seg];
  return name ? `Loading ${name}…` : null;
}

const Track = styled('div')(({ theme }) => ({
  position: 'relative',
  height: 6,
  borderRadius: 3,
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
}));

// Indeterminate mode: a gradient segment sweeping across the track.
const IndeterminateBar = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '40%',
  borderRadius: 3,
  background: EVORA_GRADIENT,
  '@keyframes evoraLoaderSweep': {
    '0%': { left: '-40%' },
    '100%': { left: '100%' },
  },
  animation: 'evoraLoaderSweep 1.3s cubic-bezier(0.65, 0, 0.35, 1) infinite',
});

// Determinate mode: a gradient fill whose width tracks `value`.
const DeterminateBar = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  borderRadius: 3,
  background: EVORA_GRADIENT,
  transition: 'width 0.3s ease',
});

/**
 * Branded loading indicator used as the app's Suspense fallback: the Evora
 * logo above an animated gradient progress bar that matches the Login button.
 *
 * Props:
 *  - value       number 0-100 for a real progress bar; omit for an
 *                indeterminate sweep (the usual "still loading" case).
 *  - label       optional caption rendered under the bar.
 *  - fullScreen  center it in a full-viewport overlay (default true; this is
 *                the Suspense/page-load case). Pass false to drop it inline.
 *  - logoWidth   logo width in px (default 180).
 *  - barWidth    bar width in px (default 220).
 *  - sx          extra styles merged onto the inner stack.
 */
export default function SuspenseLoader({
  value,
  label,
  fullScreen = true,
  logoWidth = 180,
  barWidth = 220,
  sx,
  ...rest
}) {
  const determinate = typeof value === 'number';
  const clamped = determinate ? Math.min(100, Math.max(0, value)) : 0;

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        ...sx,
      }}
      {...rest}
    >
      <Box
        component="img"
        src={EvoraLogo}
        alt="Evora"
        sx={{ width: logoWidth, maxWidth: '60vw', height: 'auto', userSelect: 'none' }}
        draggable={false}
      />
      <Track sx={{ width: barWidth, maxWidth: '70vw' }}>
        {determinate
          ? <DeterminateBar style={{ width: `${clamped}%` }} />
          : <IndeterminateBar />}
      </Track>
      {label != null && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
      )}
    </Box>
  );

  if (!fullScreen) return content;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      {content}
    </Box>
  );
}
